import { useState, useEffect, useRef, useCallback } from "react";

export const useDraggableWidget = (storageKey, defaultOffset = { bottom: 24, right: 24 }, width = 56, height = 56) => {
  const [position, setPosition] = useState(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (typeof parsed.x === "number" && typeof parsed.y === "number") {
          return parsed;
        }
      }
    } catch (e) {}

    // Default right/bottom based on window size
    const initialX = Math.max(16, (window.innerWidth || 360) - defaultOffset.right - width);
    const initialY = Math.max(16, (window.innerHeight || 640) - defaultOffset.bottom - height);
    return { x: initialX, y: initialY };
  });

  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef({
    startX: 0,
    startY: 0,
    initialX: 0,
    initialY: 0,
    moved: false,
  });

  // Clamp position to viewport bounds with margins
  const clampPosition = useCallback((x, y) => {
    const margin = 12;
    const maxX = Math.max(margin, (window.innerWidth || 360) - width - margin);
    const maxY = Math.max(margin, (window.innerHeight || 640) - height - margin);
    return {
      x: Math.min(maxX, Math.max(margin, x)),
      y: Math.min(maxY, Math.max(margin, y)),
    };
  }, [width, height]);

  // Adjust position on window resize
  useEffect(() => {
    const handleResize = () => {
      setPosition((prev) => clampPosition(prev.x, prev.y));
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [clampPosition]);

  const handlePointerDown = useCallback((e) => {
    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    dragRef.current = {
      startX: clientX,
      startY: clientY,
      initialX: position.x,
      initialY: position.y,
      moved: false,
    };
    setIsDragging(true);
  }, [position.x, position.y]);

  const handlePointerMove = useCallback((e) => {
    if (!isDragging) return;

    const clientX = e.touches ? e.touches[0].clientX : e.clientX;
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;

    const dx = clientX - dragRef.current.startX;
    const dy = clientY - dragRef.current.startY;

    if (Math.hypot(dx, dy) > 5) {
      dragRef.current.moved = true;
    }

    const nextX = dragRef.current.initialX + dx;
    const nextY = dragRef.current.initialY + dy;

    const clamped = clampPosition(nextX, nextY);
    setPosition(clamped);
  }, [isDragging, clampPosition]);

  const handlePointerUp = useCallback(() => {
    if (!isDragging) return;
    setIsDragging(false);

    // Save position
    try {
      localStorage.setItem(storageKey, JSON.stringify(position));
    } catch (e) {}
  }, [isDragging, position, storageKey]);

  useEffect(() => {
    if (isDragging) {
      const onMove = (e) => handlePointerMove(e);
      const onEnd = () => handlePointerUp();

      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onEnd);
      window.addEventListener("touchmove", onMove, { passive: false });
      window.addEventListener("touchend", onEnd);

      return () => {
        window.removeEventListener("mousemove", onMove);
        window.removeEventListener("mouseup", onEnd);
        window.removeEventListener("touchmove", onMove);
        window.removeEventListener("touchend", onEnd);
      };
    }
  }, [isDragging, handlePointerMove, handlePointerUp]);

  return {
    position,
    isDragging,
    hasMoved: dragRef.current.moved,
    bindPointer: {
      onMouseDown: handlePointerDown,
      onTouchStart: handlePointerDown,
    },
    wasDragged: () => dragRef.current.moved,
  };
};
